import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import QRCode from "react-qr-code";
import QRCodeLib from "qrcode";
import { jsPDF } from "jspdf";
import logo from "../../../../assets/logo128x128.png";

const PDCertificateGenerator = () => {
    const authState = useSelector((state) => state.authReducer);
    const user = authState?.user;

    const [showPreview, setShowPreview] = useState(true);
    const [loading, setLoading] = useState(false);

    // -----------------------------
    // SOURCE OF TRUTH (USER DATA)
    // -----------------------------
    const certificateData = useMemo(() => {
        return {
            name: user?.name || "Merchant Name",
            code: user?.think5whyscode || "T5WXXXX",
            role: user?.role || "MERCHANT",
            city: user?.city || "-",
            area: user?.area || "-",
            shop: user?.remarks || "-",
            type: "SHOP_VERIFICATION_CERTIFICATE",
            brand: "PurpleStreak + Think5Whys",
            issuedAt: new Date().toISOString(),
        };
    }, [user]);

    // -----------------------------
    // QR DATA (OFFLINE JSON MODE)
    // -----------------------------
    const qrJson = useMemo(() => {
        return JSON.stringify({
            type: "PURPLESTREAK_MERCHANT_VERIFY",
            code: certificateData.code,
            name: certificateData.name,
            role: certificateData.role,
            city: certificateData.city,
            area: certificateData.area,
            shop: certificateData.shop,
            badge: "AUTO_STREAK_VERIFIED",
        });
    }, [certificateData]);

    // -----------------------------
    // PDF DOWNLOAD
    // -----------------------------
    const downloadPDF = async () => {
        setLoading(true);

        try {
            const pdf = new jsPDF("p", "mm", "a4");
            const pageWidth = 210;

            // ---------------- BORDER (PURPLE THEME)
            pdf.setDrawColor(128, 0, 128);
            pdf.setLineWidth(2);
            pdf.rect(10, 10, 190, 277);

            pdf.setLineWidth(0.5);
            pdf.rect(14, 14, 182, 269);

            // ---------------- TITLE
            pdf.setFont("helvetica", "bold");
            pdf.setFontSize(20);
            pdf.setTextColor(128, 0, 128);
            pdf.text("SHOP VERIFICATION CERTIFICATE", 105, 35, {
                align: "center",
            });

            // ---------------- BRANDING
            pdf.setFontSize(10);
            pdf.setTextColor(60, 60, 60);
            pdf.text(
                "PurpleStreak Merchant Network • Think5Whys Global DeepTech Solutions",
                105,
                42,
                { align: "center" }
            );

            // ---------------- USER NAME
            pdf.setFontSize(22);
            pdf.setTextColor(0, 0, 0);
            pdf.text(certificateData.name, 105, 70, {
                align: "center",
            });

            // ---------------- CODE
            pdf.setFontSize(11);
            pdf.setTextColor(100, 100, 100);
            pdf.text(`Merchant Code: ${certificateData.code}`, 105, 80, {
                align: "center",
            });

            // ---------------- DETAILS
            pdf.setFontSize(12);
            pdf.setTextColor(50, 50, 50);
            pdf.text(`City: ${certificateData.city}`, 105, 100, {
                align: "center",
            });

            pdf.text(`Area: ${certificateData.area}`, 105, 110, {
                align: "center",
            });

            pdf.text(`Entity: ${certificateData.shop}`, 105, 120, {
                align: "center",
            });

            pdf.text(`Role: ${certificateData.role}`, 105, 130, {
                align: "center",
            });

            // ---------------- LOGO
            const img = new Image();
            img.src = logo;

            await new Promise((res) => (img.onload = res));
            pdf.addImage(img, "PNG", 92, 130, 25, 25);

            // ---------------- QR
            const qrImage = await QRCodeLib.toDataURL(qrJson);
            pdf.addImage(qrImage, "PNG", 80, 160, 50, 50);

            // ---------------- BADGE
            pdf.setFontSize(12);
            pdf.setTextColor(0, 128, 0);
            pdf.text("VERIFIED MERCHANT", 105, 225, {
                align: "center",
            });

            // ---------------- FOOTER
            pdf.setFontSize(9);
            pdf.setTextColor(120, 120, 120);
            pdf.text(
                "This certificate is system generated and digitally verified",
                105,
                255,
                { align: "center" }
            );

            pdf.save(
                `PurpleStreak-Merchant-Certificate-${certificateData.code}.pdf`
            );
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // -----------------------------
    // SHARE
    // -----------------------------
    const shareWhatsApp = () => {
        const msg = `
🟣 PurpleStreak Merchant Verified

Name: ${certificateData.name}
Code: ${certificateData.code}
Role: ${certificateData.role}
City: ${certificateData.city}

🟣 Auto-Streak Verified Merchant
Think5Whys Global Network
        `.trim();

        window.open(
            `https://wa.me/?text=${encodeURIComponent(msg)}`,
            "_blank"
        );
    };

    // -----------------------------
    // UI
    // -----------------------------
    return (
        <div className="bg-gray-900 border-t-2 border-purple-500 rounded-xl p-4 text-white space-y-3">

            <h2 className="text-purple-300 font-bold text-lg">
                🟣 Merchant Shop Verification Certificate
            </h2>

            {/* ACTIONS */}
            <div className="flex gap-2">
                <button
                    onClick={downloadPDF}
                    className="bg-purple-600 px-3 py-1 rounded text-sm"
                >
                    {loading ? "Generating..." : "Download PDF"}
                </button>

                <button
                    onClick={shareWhatsApp}
                    className="bg-green-600 px-3 py-1 rounded text-sm"
                >
                    WhatsApp Share
                </button>

                <button
                    onClick={() => setShowPreview((p) => !p)}
                    className="bg-gray-700 px-3 py-1 rounded text-sm"
                >
                    {showPreview ? "Hide" : "Preview"}
                </button>
            </div>

            {/* PREVIEW CARD */}
            {showPreview && (
                <div className="bg-black border border-purple-500 rounded-xl p-4 text-center">
                    <div className="text-purple-300 font-bold text-lg">
                        SHOP VERIFIED MERCHANT
                    </div>

                    <div className="text-white font-semibold mt-2">
                        {certificateData.name}
                    </div>

                    <div className="text-gray-400 text-xs">
                        {certificateData.code}
                    </div>

                    <div className="text-xs text-gray-400 mt-1">
                        {certificateData.city} • {certificateData.area}
                    </div>

                    <div className="text-xs text-gray-400 mt-1">
                        {certificateData.shop}
                    </div>

                    {/* QR */}
                    <div className="mt-3 flex justify-center bg-white p-2 rounded">
                        <QRCode value={qrJson} size={110} />
                    </div>

                    {/* BADGE */}
                    <div className="mt-3 text-green-400 text-xs font-bold">
                        🟣 AUTO-STREAK VERIFIED
                    </div>
                </div>
            )}
        </div>
    );
};

export default PDCertificateGenerator;